/**
 * Pure logic behind the boat card — a port of Flutter's `_CompactObservationPopup`
 * (`lib/features/home_screen/presentation/tools/home_screen_observation_popup.dart`).
 *
 * No Angular, no I/O: the component renders what `buildCardView` returns and the
 * host acts on what `resolveChatTarget` returns. Keeping it pure is what lets the
 * who-is-me / who-gets-the-chat rules be checked without a browser.
 */
import { depthFromMeters, speedFromMs, tempFromC } from './unit-formatter.util';
import { formatCoordinatePair } from './coordinate-format.util';

// ── Types ───────────────────────────────────────────────────────────────────

export interface CardParticipant {
  profileId?: string | null;
  name?: string | null;
  imageUrl?: string | null;
  vesselName?: string | null;
  role?: string | null;
}

/** Tap-site arguments — one-to-one with the Flutter widget's constructor. */
export interface CardParams {
  /** `primaryVesselRefId` */
  vesselId?: string | null;
  observationId?: string | null;
  /** `externalLabel` — vessel name shown in the header. */
  vesselName?: string | null;
  ownerName?: string | null;
  ownerUserId?: string | null;
  /** Raw UTC timestamp, preferred over the observation's own. */
  lastSeen?: string | null;
  tripName?: string | null;
  rideAlongTripId?: string | null;
  rideAlongTripState?: string | null;
  observedTripId?: string | null;
  fallbackLat?: number | null;
  fallbackLon?: number | null;
  fallbackCog?: number | null;
}

export interface QuickObs {
  ts_utc: string | null;
  lat: number | null;
  lon: number | null;
  cog_deg: number | null;
  sog_ms: number | null;
  water_temp_c: number | null;
  depth_m: number | null;
  vessel_name: string | null;
  vessel_ref_id: string | null;
  ride_along_participants: CardParticipant[] | null;
  ride_along_trip_name: string | null;
  ride_along_enabled: boolean;
  trip_state: string | null;
  captain_user_id: string | null;
  trip_id: string | null;
  /** Added by the ride-along enrichment fetch. */
  ride_along_trip_id?: string | null;
}

/** Who the signed-in user is, in every shape the payloads might name them. */
export interface CardIdentity {
  /** My keycloak uid — the id chat rooms and group membership are keyed on. */
  userId?: string | null;
  /** keycloak uid, profile id, profile doc id — any of these can appear as an owner id. */
  ids: string[];
  /** Lowercased display / full / token names. */
  names: string[];
  /** Trip id of the trip I am currently running, if any. */
  activeTripId?: string | null;
}

export interface CardUnits {
  speed?: string | null;
  temperature?: string | null;
  depth?: string | null;
}

export interface ChatArgs {
  participants: CardParticipant[];
  tripName: string | null;
  captainUserId: string | null;
  tripId: string | null;
  isSelf: boolean;
  /** Participant on their own vessel — the 1-to-1 fallback chats with them. */
  vesselOwner: CardParticipant | null;
}

export interface CardView {
  headline: string | null;
  waterLine: string | null;
  position: string | null;
  participants: CardParticipant[];
  showParticipants: boolean;
  ownerName: string | null;
  ownerUserId: string | null;
  ownerImageUrl: string | null;
  isMyBoat: boolean;
  tsLabel: string | null;
  showChat: boolean;
  chatArgs: ChatArgs;
  hasReadingsAbove: boolean;
  hasIdentity: boolean;
}

export type ChatTarget =
  | { kind: 'chat-list' }
  | {
      kind: 'ride-along-group';
      name: string;
      memberIds: string[];
      ownerKeycloakId: string | null;
      groupImageUrl: string;
      tripId: string | null;
      communityName: string;
    }
  | { kind: 'direct'; name: string; targetUserId: string | null }
  | { kind: 'none' };

// ── Small helpers ───────────────────────────────────────────────────────────

/** Trimmed non-empty string, else null. "0" is the API's placeholder user id ("no owner"), never a real value. */
const clean = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s && s !== '0' ? s : null;
};

const lower = (v: string | null | undefined): string => (v ?? '').trim().toLowerCase();

const num = (fields: Record<string, any>, keys: string[]): number | null => {
  for (const k of keys) if (typeof fields[k] === 'number') return fields[k];
  return null;
};

/** `Text(AvatarInitials.fromName)` — first letters of the first two words, or first two of a single word. */
export function avatarInitials(name: string | null | undefined, fallback = 'U'): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  if (words.length === 1) return Array.from(words[0]).slice(0, 2).join('').toUpperCase();
  return Array.from(fallback.trim() || 'U').slice(0, 2).join('').toUpperCase();
}

/** `_formatLastSeen` */
export function formatLastSeen(ts: string, now: Date = new Date()): string {
  const s = ts.trim();
  const hasTz = /Z$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const dt = new Date(hasTz ? s : `${s}Z`);
  if (isNaN(dt.getTime())) return ts;
  const diffMs = now.getTime() - dt.getTime();
  if (diffMs >= 0) {
    const secs = Math.floor(diffMs / 1000);
    if (secs < 60) return 'Active just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `Active ${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Active ${hours}h ago`;
  }
  const time = dt.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${time}, ${dt.getMonth() + 1}/${dt.getDate()}/${String(dt.getFullYear()).slice(-2)}`;
}

// ── Extraction (`_extractQuickObsFields`) ────────────────────────────────────

/**
 * `mapParticipant` converts a raw payload row; injected so the web's existing
 * `RideAlongService` mapper (itself a port of `RideAlongParticipant.fromJson`)
 * stays the single implementation.
 */
export function extractQuickObs(
  obs: any,
  mapParticipant: (row: any) => CardParticipant,
): QuickObs {
  let ts = typeof obs?.ts_utc === 'string' ? obs.ts_utc : null;

  let vesselRefId: string | null = null;
  let vesselName: string | null = null;
  const v = obs?.vessel_ref ?? obs?.vessel ?? obs?.vesselRef;
  if (v && typeof v === 'object') {
    vesselRefId = clean(v.reference_id ?? v.referenceId ?? v.id ?? v._id);
    vesselName = clean(v.vessel_name ?? v.vesselName ?? v.name ?? v.title);
  }
  vesselName ??= clean(obs?.vessel_name ?? obs?.vesselName ?? obs?.name);
  vesselRefId ??= clean(obs?.vessel_ref_id ?? obs?.vesselRefId);

  let lat: number | null = null;
  let lon: number | null = null;
  let cog: number | null = null;
  let sogMs: number | null = null;
  let waterTempC: number | null = null;
  let depthM: number | null = null;

  const pgns = Array.isArray(obs?.pgns) ? obs.pgns : [];
  for (const p of pgns) {
    if (!p || typeof p !== 'object') continue;
    const fields = p.fields && typeof p.fields === 'object' ? p.fields : null;
    if (ts == null && typeof p.ts_utc === 'string') ts = p.ts_utc;
    if (!fields) continue;
    lat ??= num(fields, ['latitude_deg', 'lat_deg', 'latitude', 'lat']);
    lon ??= num(fields, ['longitude_deg', 'lon_deg', 'longitude', 'lon', 'lng']);
    cog ??= num(fields, ['cog_deg', 'cog', 'course_over_ground']);
    sogMs ??= num(fields, ['sog_knots', 'sog', 'speed_over_ground']);
    waterTempC ??= num(fields, ['water_temp_c', 'water_temp', 'water_temperature', 'temp_c']);
    depthM ??= num(fields, ['depth_m', 'depth', 'water_depth']);
  }

  // The ride-along fetch returns a flat structure; other paths nest it under trip_details.
  const trip: any = obs?.trip_details && typeof obs.trip_details === 'object' ? obs.trip_details : obs;

  const participantsRaw = trip?.ride_along_participants ?? trip?.participants;
  let participants: CardParticipant[] | null = null;
  if (participantsRaw != null) {
    participants = Array.isArray(participantsRaw)
      ? participantsRaw.filter((p: any) => p && typeof p === 'object').map(mapParticipant)
      : [];
  }

  const audit = obs?.audit_data ?? trip?.audit_data;
  let captainUserId: string | null = null;
  if (audit && typeof audit === 'object') {
    captainUserId = clean(
      audit.create_user_id ?? audit.createUser ?? audit.createUserId ?? audit.owner_user_id ?? audit.ownerUserId,
    );
  }
  captainUserId ??= clean(
    obs?.captain_user_id ?? trip?.captain_user_id ?? trip?.owner_user_id ?? trip?.ownerUserId ?? trip?.userId ?? trip?.user_id,
  );

  return {
    ts_utc: ts,
    lat,
    lon,
    cog_deg: cog,
    sog_ms: sogMs,
    water_temp_c: waterTempC,
    depth_m: depthM,
    vessel_name: vesselName,
    vessel_ref_id: vesselRefId,
    ride_along_participants: participants,
    ride_along_trip_name: clean(trip?.ride_along_trip_name ?? trip?.trip_name ?? trip?.name),
    ride_along_enabled: trip?.ride_along_enabled === true || trip?.ride_along === true,
    trip_state: clean(trip?.trip_state ?? trip?.state ?? trip?.tripState),
    captain_user_id: captainUserId,
    trip_id: clean(trip?.trip_id ?? trip?.tripId ?? obs?.trip_id ?? obs?.tripId),
  };
}

// ── View model (`_buildObservationFields`) ───────────────────────────────────

const IN_PROGRESS = new Set(['in_progress', 'progress', 'inprogress', 'live', 'active']);

export function buildCardView(
  quick: QuickObs,
  params: CardParams,
  me: CardIdentity,
  units: CardUnits,
  chatAvailable: boolean,
): CardView {
  const lat = quick.lat ?? params.fallbackLat ?? null;
  const lon = quick.lon ?? params.fallbackLon ?? null;
  const cog = quick.cog_deg ?? params.fallbackCog ?? null;
  const sogMs = quick.sog_ms;

  const participants = [...(quick.ride_along_participants ?? [])];
  // Stable sort: captain first, everyone else keeps arrival order.
  participants.sort((a, b) => {
    const ac = lower(a.role) === 'captain';
    const bc = lower(b.role) === 'captain';
    return ac === bc ? 0 : ac ? -1 : 1;
  });

  const rawTs = clean(params.lastSeen) ?? clean(quick.ts_utc);
  const tsLabel = rawTs ? formatLastSeen(rawTs) : null;

  const hasSogOrCog = sogMs != null || cog != null;
  const sogCogParts = [
    ...(sogMs != null ? [speedFromMs(sogMs, units.speed, 1, '--').text] : []),
    ...(cog != null ? [`${cog.toFixed(0)}°`] : []),
  ];
  const waterParts = [
    ...(quick.water_temp_c != null ? [`${tempFromC(quick.water_temp_c, units.temperature, 0, '--').text} water temp`] : []),
    ...(quick.depth_m != null ? [`${depthFromMeters(quick.depth_m, units.depth, 1, '--').text} depth`] : []),
  ];

  const captain = participants.find((p) => lower(p.role) === 'captain') ?? null;

  // On a ride-along each participant can be on their OWN vessel, while the fetched
  // context is the whole trip's. When a participant's vessel matches the header,
  // that participant is who this popup is about.
  const headerVesselName = lower(clean(params.vesselName) ?? quick.vessel_name ?? '');
  const vesselOwner = headerVesselName
    ? participants.find((p) => lower(p.vesselName) === headerVesselName && !!clean(p.name)) ?? null
    : null;
  const captainVessel = lower(captain?.vesselName);
  // Only trusted when the captain is KNOWN to be on a different boat and the
  // participant has an id, so name, profile link and chat target name one person.
  const ownerIsOtherParticipant =
    !!vesselOwner && !!captain && vesselOwner !== captain && !!captainVessel &&
    captainVessel !== headerVesselName && !!clean(vesselOwner.profileId);

  const ownerName = ownerIsOtherParticipant
    ? vesselOwner!.name!.trim()
    : clean(params.ownerName) ?? clean(captain?.name);

  const ownerUserId =
    [
      ownerIsOtherParticipant ? vesselOwner!.profileId : null,
      params.ownerUserId,
      quick.captain_user_id,
      captain?.profileId,
    ]
      .map((v) => clean(v))
      .find((v) => !!v) ?? null;

  const rideAlongEnabled = quick.ride_along_enabled;
  const tripStateRaw = lower(quick.trip_state);
  const shouldShowRideAlong = rideAlongEnabled && IN_PROGRESS.has(tripStateRaw);
  // A solo rider is already named in the owner row; only introduce a crew when someone else is aboard.
  const showParticipants = shouldShowRideAlong && quick.ride_along_participants != null && participants.length > 1;

  const effectiveOwnerUserId =
    !ownerIsOtherParticipant && clean(params.ownerUserId) ? clean(params.ownerUserId)! : ownerUserId ?? '';
  const ownerNameLower = lower(ownerName);

  const candidateTripId =
    quick.ride_along_trip_id ?? quick.trip_id ?? params.rideAlongTripId ?? params.observedTripId ?? null;
  const isMyActiveTrip = !!me.activeTripId && !!candidateTripId && me.activeTripId === candidateTripId;

  // Decide by id whenever there is one; a display-name collision must not make
  // someone else's boat look like ours and hide chat.
  const personIsMe = (id?: string | null, name?: string | null): boolean => {
    const tid = clean(id);
    if (tid) return me.ids.includes(tid);
    const tname = lower(name);
    return !!tname && me.names.includes(tname);
  };

  const isCaptainMe = !!captain && personIsMe(captain.profileId, captain.name);
  const ownerIsMe = personIsMe(effectiveOwnerUserId, ownerNameLower);
  const isMyBoat = isMyActiveTrip || isCaptainMe || ownerIsMe;
  const isParticipant = participants.some((p) => personIsMe(p.profileId, p.name));

  const isRideAlongTrip =
    shouldShowRideAlong ||
    !!clean(params.rideAlongTripId) ||
    !!clean(params.observedTripId) ||
    !!clean(quick.ride_along_trip_id) ||
    (participants.length > 0 && (rideAlongEnabled || participants.length > 1));

  const hasChatTarget = !isMyBoat && (!!effectiveOwnerUserId || !!ownerNameLower);

  let showChat: boolean;
  if (isRideAlongTrip && (isMyBoat || isParticipant)) {
    const state = lower(params.rideAlongTripState) || tripStateRaw;
    showChat = !(state === 'ended' || state === 'completed');
  } else {
    // Outside viewers of a vessel on a trip get the 1-to-1 chat with the owner.
    showChat = hasChatTarget;
  }
  showChat = showChat && chatAvailable;

  const position = lat != null && lon != null ? formatCoordinatePair(lat, lon) : null;
  const hasReadingsAbove = hasSogOrCog || waterParts.length > 0 || position != null || showParticipants;
  const ownerImageUrl = ownerIsOtherParticipant ? clean(vesselOwner?.imageUrl) : clean(captain?.imageUrl);

  return {
    headline: hasSogOrCog ? sogCogParts.join(' • ') : null,
    waterLine: waterParts.length ? waterParts.join('  ·  ') : null,
    position,
    participants,
    showParticipants,
    ownerName,
    ownerUserId,
    ownerImageUrl,
    isMyBoat,
    tsLabel,
    showChat,
    chatArgs: {
      participants,
      tripName: quick.ride_along_trip_name ?? clean(params.tripName),
      captainUserId: quick.captain_user_id ?? captain?.profileId ?? ownerUserId,
      tripId: candidateTripId,
      isSelf: isMyBoat,
      vesselOwner: ownerIsOtherParticipant ? vesselOwner : null,
    },
    hasReadingsAbove,
    hasIdentity: !!ownerName || !!ownerUserId || tsLabel != null || showChat,
  };
}

// ── Chat routing (`_navigateToChat`) ─────────────────────────────────────────

export function resolveChatTarget(
  args: ChatArgs,
  params: CardParams,
  me: CardIdentity,
  externalLabel: string | null,
): ChatTarget {
  const { participants } = args;
  const currentUserId = clean(me.userId) ?? '';
  const captain = participants.find((p) => lower(p.role) === 'captain') ?? null;

  const matchesMe = (id?: string | null, name?: string | null): boolean => {
    const tid = clean(id);
    if (tid) return me.ids.includes(tid);
    const tname = lower(name);
    return !!tname && me.names.includes(tname);
  };

  const userIsCaptain =
    participants.some((p) => matchesMe(p.profileId, p.name) && lower(p.role) === 'captain') ||
    (!!captain && matchesMe(captain.profileId, captain.name));

  const ownerUserId = clean(params.ownerUserId) ?? clean(args.captainUserId) ?? clean(captain?.profileId);
  const captainOwnerName = clean(params.ownerName) ?? clean(captain?.name);

  const roomTripId =
    clean(args.tripId) ??
    clean(params.observedTripId) ??
    clean(params.rideAlongTripId) ??
    // My own trip only when tapping myself; for anyone else it would turn a chat
    // with them into a chat for my trip.
    (args.isSelf ? clean(me.activeTripId) : null);

  const isSessionTripCaptain = !!me.activeTripId && !!roomTripId && me.activeTripId === roomTripId;
  const isMe = args.isSelf || userIsCaptain || isSessionTripCaptain || matchesMe(ownerUserId, captainOwnerName);

  const memberIds = participants
    .map((p) => clean(p.profileId))
    .filter((id): id is string => !!id && !matchesMe(id, null));
  const hasOtherParticipants = memberIds.length > 0;
  if (currentUserId) memberIds.push(currentUserId);

  if (isMe && !hasOtherParticipants && !roomTripId) return { kind: 'chat-list' };

  const amIParticipant = participants.some((p) => matchesMe(p.profileId, p.name));
  const isUserInRideAlong = isMe || amIParticipant;
  const isRideAlong = !!roomTripId || participants.length > 0;

  // Outside viewers (not captain/participant) fall through to the 1-to-1 chat below.
  if (isRideAlong && isUserInRideAlong) {
    const effectiveTripName = clean(args.tripName) ?? clean(params.tripName);
    const groupChatName = effectiveTripName ?? clean(externalLabel) ?? "Ride Along Trip's Chat";
    const captainProfileId = clean(captain?.profileId);
    const captainUserId = clean(args.captainUserId);
    return {
      kind: 'ride-along-group',
      name: groupChatName,
      memberIds,
      ownerKeycloakId: captainProfileId ?? captainUserId ?? ownerUserId ?? (currentUserId || null),
      groupImageUrl: clean(captain?.imageUrl) ?? '',
      tripId: roomTripId,
      communityName: clean(params.tripName) ?? groupChatName,
    };
  }

  const name = clean(args.vesselOwner?.name) ?? captainOwnerName ?? clean(params.ownerName);
  if (!name) return { kind: 'none' };
  return {
    kind: 'direct',
    name,
    targetUserId: clean(args.vesselOwner?.profileId) ?? ownerUserId,
  };
}
